import { type FC, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { MenuNode, MenuRenderer } from '../../../common/hooks/useMenuState';
import type { EcommerceSideMenuProps } from './EcommerceSideMenu.types';

import { appConfig } from '../../../../appConfig';
import { SkipLink, SkipLinkLayout } from '../../../base/components/SkipLink';
import SideMenu from '../../../common/components/molecules/SideMenu/SideMenu';
import { useIsEligibleForFavoriteProducts } from '../../../common/hooks/useFavoriteProducts';
import { formatShowAllCategoryLabel } from '../../utils/showAllCategoryLabel';
import { useEcommerceSideMenu } from './EcommerceSideMenu.hooks';
import FavoriteProductsSideMenuItemBody from './FavoriteProductsSideMenuItemBody/FavoriteProductsSideMenuItemBody';

const decorateMenu = (
    root: MenuNode,
    isEligibleForFavoriteProducts: boolean,
): MenuNode | undefined => {
    const decorateNode = (node: MenuNode): MenuNode | undefined => {
        if (
            !isEligibleForFavoriteProducts &&
            node.id === appConfig.coopSettings.ecommerce.favoriteProductsPage?.contentId
        )
            return undefined;

        let renderBody: MenuRenderer | undefined;

        switch (node.id) {
            case appConfig.coopSettings.ecommerce.favoriteProductsPage?.contentId:
                renderBody = () => <FavoriteProductsSideMenuItemBody node={node} />;
                break;

            default:
                break;
        }

        const nodes = node.nodes?.map(decorateNode).filter(Boolean) ?? [];

        return {
            ...node,
            renderBody,
            nodes,
            ancestors: node.ancestors?.map(decorateNode).filter(Boolean) ?? [],
            displayName: nodes.length ? formatShowAllCategoryLabel(node.name) : undefined,
        };
    };

    return decorateNode(root);
};

export const useDecoratedMenu = (contentMenu?: MenuNode) => {
    const isEligibleForFavoriteProducts = useIsEligibleForFavoriteProducts();

    return useMemo(() => {
        if (contentMenu) return decorateMenu(contentMenu, isEligibleForFavoriteProducts);
    }, [contentMenu, isEligibleForFavoriteProducts]);
};

const EcommerceSideMenu: FC<React.PropsWithChildren<EcommerceSideMenuProps>> = (props) => {
    const {
        expandedNodesTrail,
        activeNodesTrail,
        onExpandClick,
        onLinkClick,
        menu: contentMenu,
    } = useEcommerceSideMenu(props);

    const menu = useDecoratedMenu(contentMenu);

    const { pathname } = useLocation();

    if (!menu) {
        return null;
    }

    return (
        <SkipLinkLayout
            skipTriggerSlot={
                <SkipLink href="#ecomSections" align="center">
                    Hoppa över kategorier
                </SkipLink>
            }
        >
            <SideMenu
                currentPath={pathname}
                label="Kategorier"
                activeNodesTrail={activeNodesTrail}
                expandedNodesTrail={expandedNodesTrail}
                rootNode={menu}
                onLinkClick={onLinkClick}
                onExpandClick={onExpandClick}
            />
        </SkipLinkLayout>
    );
};

export default EcommerceSideMenu;
